import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Coffee, ShoppingBag, ConciergeBell, MapPin, BadgeCheck, Tag, Store, UtensilsCrossed, CalendarCheck } from "lucide-react";
import api from "../lib/api";
import { Spinner, Pill } from "./common";

const CAT_META = {
  food: { label: "Food & Beverage", icon: Coffee, color: "text-orange-600" },
  retail: { label: "Retail", icon: ShoppingBag, color: "text-blue-600" },
  services: { label: "Services", icon: ConciergeBell, color: "text-teal-600" },
};

export const locationTrail = (b) =>
  [b.terminal, b.concourse, b.gate_area].filter(Boolean).join(" → ") || "Location not yet verified";

export function BusinessCard({ b }) {
  const meta = CAT_META[b.category] || CAT_META.services;
  const Icon = meta.icon;
  return (
    <Link to={`/business/${b.id}`} data-testid={`business-card-${b.id}`}
      className="group border border-zinc-300 bg-white p-4 hover:border-primary hover:shadow-lg transition-all flex flex-col">
      <div className="flex items-center gap-2 text-xs">
        <Icon className={`w-4 h-4 ${meta.color}`} />
        <span className="font-mono uppercase tracking-wide text-muted-foreground">{b.subcategory || meta.label}</span>
        {b.verified && <BadgeCheck className="w-4 h-4 text-emerald-600" title="Verified business" />}
      </div>
      <div className="font-heading font-900 text-lg mt-1 leading-snug group-hover:text-primary transition-colors">{b.name}</div>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1.5">
        <MapPin className="w-3.5 h-3.5 shrink-0" /> {locationTrail(b)}
        {b.security_side && <Pill tone={b.security_side === "after" ? "blue" : "amber"}>{b.security_side === "after" ? "After security" : "Before security"}</Pill>}
      </div>
      {b.description && <p className="text-sm text-zinc-700 mt-2 line-clamp-2">{b.description}</p>}
      <div className="flex items-center gap-2 mt-auto pt-2">
        {b.local_brand && <Pill tone="green">Local favorite</Pill>}
        {b.has_menu && <span className="inline-flex items-center gap-1 text-xs font-700 bg-violet-50 border border-violet-300 text-violet-700 px-1.5 py-0.5" data-testid={`menu-pill-${b.id}`}><UtensilsCrossed className="w-3 h-3" /> Menu</span>}
        {b.can_book && <span className="inline-flex items-center gap-1 text-xs font-700 bg-emerald-50 border border-emerald-300 text-emerald-700 px-1.5 py-0.5" data-testid={`book-pill-${b.id}`}><CalendarCheck className="w-3 h-3" /> Book / Order</span>}
        {b.price_level && <span className="text-xs text-muted-foreground font-mono">{"$".repeat(b.price_level)}</span>}
      </div>
    </Link>
  );
}

/* "Dining & Shopping" tab content for an airport profile. */
export default function AirportShops({ iata, airportName }) {
  const [items, setItems] = useState(null);
  const [deals, setDeals] = useState([]);
  const [cat, setCat] = useState("all");
  const [terminal, setTerminal] = useState("all");
  const [security, setSecurity] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!iata) return;
    api.get("/marketplace/businesses", { params: { airport: iata } })
      .then((r) => setItems(r.data.items)).catch(() => setItems([]));
    api.get("/marketplace/deals", { params: { airport: iata } })
      .then((r) => setDeals(r.data.items)).catch(() => {});
  }, [iata]);

  const terminals = useMemo(() => [...new Set((items || []).map((b) => b.terminal).filter(Boolean))].sort(), [items]);
  const filtered = useMemo(() => (items || []).filter((b) =>
    (cat === "all" || b.category === cat) &&
    (terminal === "all" || b.terminal === terminal) &&
    (security === "all" || b.security_side === security) &&
    (!q || `${b.name} ${b.subcategory} ${b.description}`.toLowerCase().includes(q.toLowerCase()))
  ), [items, cat, terminal, security, q]);

  if (items === null) return <Spinner />;
  if (!items.length) return (
    <div className="border border-dashed border-zinc-300 py-16 text-center text-muted-foreground">
      <Store className="w-10 h-10 mx-auto mb-3 opacity-50" />
      Concessions for this airport haven't been added yet. <Link to="/marketplace" className="text-primary underline">Browse the marketplace</Link>.
    </div>
  );

  const groups = ["food", "retail", "services"].map((c) => [c, filtered.filter((b) => b.category === c)]).filter(([, l]) => l.length);

  return (
    <div data-testid="airport-shops">
      {deals.length > 0 && (
        <div className="mb-6" data-testid="deals-spotlight">
          <h3 className="font-heading font-900 text-lg mb-2 flex items-center gap-2 text-emerald-700">
            <Tag className="w-5 h-5" /> Deals Spotlight <span className="text-sm font-500 text-muted-foreground">({deals.length} active)</span>
          </h3>
          <div className="space-y-2">
            {deals.slice(0, 6).map((d) => (
              <Link key={d.id} to={`/business/${d.business_id}`} className="flex items-center gap-3 border border-emerald-300 bg-emerald-50 px-4 py-2.5 hover:shadow transition-shadow" data-testid={`airport-deal-${d.id}`}>
                <Tag className="w-4 h-4 text-emerald-700 shrink-0" />
                <span className="font-700 text-sm">{d.title}</span>
                <span className="text-xs text-muted-foreground ml-auto">{d.business_name}{d.terminal ? ` · ${d.terminal}` : ""}{d.valid_until ? ` · until ${d.valid_until}` : ""}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {items.some((b) => b.has_menu || b.can_book) && (
        <div className="mb-6" data-testid="minisite-spotlight">
          <h3 className="font-heading font-900 text-lg mb-2 flex items-center gap-2 text-violet-700">
            <UtensilsCrossed className="w-5 h-5" /> Menus &amp; Reservations
          </h3>
          <div className="flex flex-wrap gap-2">
            {items.filter((b) => b.has_menu || b.can_book).slice(0, 8).map((b) => (
              <Link key={b.id} to={`/business/${b.id}`} data-testid={`spotlight-biz-${b.id}`}
                className="inline-flex items-center gap-2 border border-violet-300 bg-violet-50 px-3 py-2 text-sm font-600 hover:bg-violet-100 transition-colors">
                {b.name}
                {b.has_menu && <UtensilsCrossed className="w-3.5 h-3.5 text-violet-600" />}
                {b.can_book && <CalendarCheck className="w-3.5 h-3.5 text-emerald-600" />}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {[["all", "All"], ["food", "Food & Beverage"], ["retail", "Retail"], ["services", "Services"]].map(([k, l]) => (
          <button key={k} onClick={() => setCat(k)} data-testid={`shops-filter-${k}`}
            className={`px-3.5 py-1.5 text-sm font-700 border transition-colors ${cat === k ? "bg-[#FBBC05] text-zinc-900 border-[#FBBC05]" : "bg-white border-zinc-300 hover:border-zinc-500"}`}>
            {l}
          </button>
        ))}
        {terminals.length > 0 && (
          <select value={terminal} onChange={(e) => setTerminal(e.target.value)} data-testid="shops-terminal-select"
            className="border border-zinc-300 bg-white px-3 py-1.5 text-sm">
            <option value="all">All terminals</option>
            {terminals.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <select value={security} onChange={(e) => setSecurity(e.target.value)} data-testid="shops-security-select"
          className="border border-zinc-300 bg-white px-3 py-1.5 text-sm">
          <option value="all">Before & after security</option>
          <option value="after">After security</option>
          <option value="before">Before security</option>
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search shops & dining…" data-testid="shops-search-input"
          className="border border-zinc-300 bg-white px-3 py-1.5 text-sm flex-1 min-w-[180px]" />
      </div>

      {groups.map(([c, list]) => {
        const meta = CAT_META[c];
        const Icon = meta.icon;
        return (
          <div key={c} className="mb-8">
            <h3 className={`font-heading font-900 text-lg mb-3 flex items-center gap-2 ${meta.color}`}>
              <Icon className="w-5 h-5" /> {meta.label} <span className="text-sm font-500 text-muted-foreground">({list.length})</span>
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map((b) => <BusinessCard key={b.id} b={b} />)}
            </div>
          </div>
        );
      })}
      {!groups.length && <p className="text-muted-foreground text-sm py-8 text-center">No businesses match those filters.</p>}
    </div>
  );
}
